import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Hammer, Zap, Compass } from "lucide-react";
import { TID } from "@/constants/site";
import { easeOut } from "@/lib/motion";
import { HeroVisual } from "@/components/Visuals";

const headline = ["Smarter decisions", "for people.", "Practical AI", "for business."];

const PHILOSOPHY = [
  { id: "build", n: "01", icon: Hammer, title: "Build", body: "We design and develop AI-powered applications and digital products that help people and businesses make better-informed decisions." },
  { id: "enable", n: "02", icon: Zap, title: "Enable", body: "We make practical AI accessible to small and growing businesses through solutions built around the everyday workflows that matter the most." },
  { id: "empower", n: "03", icon: Compass, title: "Empower", body: "We bring AI capability closer to everyday decisions, helping people unlock insights and make informed decisions without needing to be experts." },
];

const MARQUEE = ["Human Agency", "Pragmatic AI", "Real Decisions", "Practical & Accessible", "Ethical Governance", "Tactile Digital Products"];

const Hero = ({ onAida }) => {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="home" className="relative pt-36 pb-20 lg:pt-44 overflow-hidden">
      {/* ambient glow */}
      <div className="pointer-events-none absolute -top-40 right-0 h-[520px] w-[520px] rounded-full bg-[#43B0CE]/10 blur-[120px]" />
      <div className="pointer-events-none absolute top-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#43B0CE]/5 blur-[120px]" />
      {/* faint dot grid (reference texture) */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(rgba(67,176,206,0.14)_1px,transparent_1px)] bg-[size:22px_22px] [mask-image:radial-gradient(ellipse_at_center,black_25%,transparent_72%)]" />

      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid items-center gap-10 md:grid-cols-12">
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="badge-pill mb-8"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#43B0CE]" />
              Architecting Practical Intelligence
            </motion.div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-[4.4rem] font-extrabold tracking-tight leading-[1.04]">
              {headline.map((line, i) => (
                <span key={i} className="reveal-line block">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, ease: easeOut, delay: 0.15 + i * 0.12 }}
                  >
                    <span className={i >= 2 ? "text-[#43B0CE]" : "text-[#0B2429]"}>{line}</span>
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.75 }}
              className="mt-8 max-w-2xl text-lg sm:text-xl font-light leading-relaxed text-[#3C5156]"
            >
              AidaNova designs and builds AI-powered applications and digital products that help both small
              businesses and individual customers make smart, informed decisions.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid={TID.hero.discoverBtn}
                onClick={() => go("why")}
                className="group btn-primary px-7 py-3.5 text-sm"
              >
                Discover AidaNova
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                data-testid={TID.hero.aidaBtn}
                onClick={onAida}
                className="group btn-secondary px-7 py-3.5 text-sm"
              >
                <Sparkles className="h-4 w-4" /> Ask Aida
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.45, ease: easeOut }}
            className="relative md:col-span-5 md:-mt-14 md:-mr-16 md:-ml-10 md:pointer-events-none xl:-mt-20 xl:-mr-24 xl:-ml-16"
          >
            <HeroVisual />
            <p className="mt-4 text-center font-quote text-lg leading-snug text-[#3C5156] md:pr-16 xl:pr-24">
              AI in service of people — not the other way around.
            </p>
          </motion.div>
        </div>

        {/* Philosophy */}
        <div className="mt-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="badge-pill">
              <span className="h-1.5 w-1.5 rounded-full bg-[#43B0CE]" />
              Our Philosophy
            </span>
            <h2 className="mt-5 font-display text-2xl sm:text-3xl font-bold text-[#0B2429]">
              Three ideas guide <span className="text-[#43B0CE]">everything we build.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {PHILOSOPHY.map((p, i) => (
              <motion.div
                key={p.id}
                data-testid={TID.hero.philosophyCard(p.id)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: easeOut }}
                className="card-arch rounded-2xl p-7 flex flex-col"
              >
                <span className="icon-tile">
                  <p.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-6 font-display text-lg font-semibold text-[#0B2429]">{p.title}</h3>
                <p className="mt-3 text-[#3C5156] leading-relaxed text-[15px] flex-1">{p.body}</p>
                <span className="btn-circle mt-7 self-start">
                  <ArrowRight className="h-4 w-4" />
                </span>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-12 max-w-3xl mx-auto text-center font-quote text-xl sm:text-2xl leading-snug text-[#3C5156]"
          >
            We believe AI should be practical, accessible and designed around real human and business needs —
            not the other way around.
          </motion.p>
        </div>
      </div>

      {/* Marquee */}
      <div className="mt-20 border-y border-black/10 py-6 overflow-hidden">
        <div className="marquee-track">
          {[...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={i} className="mx-8 font-display text-2xl sm:text-3xl font-bold text-[#C4DDE1] uppercase tracking-tight flex items-center gap-8">
              {m} <span className="text-[#43B0CE]">✦</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
